import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Zap, Palmtree, Sparkles, Filter } from "lucide-react";
import { Gems } from "@/components/Gems";
import { createMolliePayment } from "@/services/mollieService";
import { toast } from "sonner";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import x2gems from "@/assets/boostx2gems.png";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

interface ShopItem {
    id: string;
    name: string;
    description: string;
    type: 'boost' | 'border';
    price_eur: number;
    metadata: any;
}

const Shop = () => {
    const { user } = useAuth();
    const [items, setItems] = useState<ShopItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [buyingStatus, setBuyingStatus] = useState<string | null>(null);
    const [ownedItemIds, setOwnedItemIds] = useState<string[]>([]);
    const [activeBoostEndDate, setActiveBoostEndDate] = useState<string | null>(null);
    const [userProfile, setUserProfile] = useState<{ avatar_url?: string; display_name?: string } | null>(null);
    const [filter, setFilter] = useState<'all' | 'owned' | 'not_owned'>('all');

    useEffect(() => {
        const fetchData = async () => {
            // Fetch shop items
            const { data: shopData, error: shopError } = await supabase
                .from('shop_items')
                .select('*')
                .eq('is_active', true)
                .eq('is_leveling', false);

            if (shopError) {
                console.error('Error fetching shop items:', shopError);
            } else {
                setItems(shopData as ShopItem[]);
            }

            // Fetch user inventory if logged in
            if (user) {
                const { data: inventoryData, error: inventoryError } = await supabase
                    .from('user_inventory')
                    .select('item_id')
                    .eq('user_id', user.id);

                if (inventoryError) {
                    console.error('Error fetching inventory:', inventoryError);
                } else {
                    setOwnedItemIds(inventoryData.map(item => item.item_id));
                }

                // Fetch active boost status
                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('boost_end_date, avatar_url, display_name')
                    .eq('id', user.id)
                    .single();

                if (profileError) {
                    console.error('Error fetching boost status:', profileError);
                } else {
                    if (profileData?.boost_end_date) {
                        const boostEndDate = new Date(profileData.boost_end_date);
                        if (boostEndDate > new Date()) {
                            setActiveBoostEndDate(profileData.boost_end_date);
                        }
                    }
                    setUserProfile({
                        avatar_url: profileData?.avatar_url,
                        display_name: profileData?.display_name
                    });
                }
            }

            setLoading(false);
        };

        fetchData();
    }, [user]);

    const handleBuy = async (item: ShopItem) => {
        if (!user) return;

        setBuyingStatus(item.id);
        try {
            let paymentType = '';

            if (item.type === 'boost') {
                paymentType = `player_boost_${item.metadata.duration_days}d`;
            } else {
                // Check if it's a standard/hardcoded border style
                const isStandardStyle = ['beta', 'gold', 'neon'].includes(item.metadata.style);
                if (isStandardStyle) {
                    paymentType = `player_border_${item.metadata.style}`;
                } else {
                    // For dynamic borders, use the generic 'player_border' type
                    // The backend SQL function handles 'player_border' by looking up item_id in metadata
                    paymentType = 'player_border';
                }
            }

            const result = await createMolliePayment(
                user.id,
                paymentType,
                false, // TODO: handle beta status if needed
                { item_id: item.id }
            );

            if (result.success && result.paymentUrl) {
                window.location.href = result.paymentUrl;
            } else {
                toast.error(result.error || "Failed to initiate payment");
            }
        } catch (error) {
            console.error('Error initiating buy:', error);
            toast.error("An unexpected error occurred");
        } finally {
            setBuyingStatus(null);
        }
    };

    const boosts = items.filter(i => i.type === 'boost').sort((a, b) => a.price_eur - b.price_eur);
    const borders = items.filter(i => i.type === 'border')
        .filter(item => {
            if (filter === 'owned') return ownedItemIds.includes(item.id);
            if (filter === 'not_owned') return !ownedItemIds.includes(item.id);
            return true;
        })
        .sort((a, b) => a.price_eur - b.price_eur);

    return (
        <Layout>
            <main className="container mx-auto px-4 py-8">
                <div className="flex flex-col gap-8">
                    <div className="text-center space-y-4">
                        <h1 className="text-4xl font-bold tracking-tight premium-gradient-text">Player Shop</h1>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Level up your profile with exclusive Gems boosts and custom avatar borders.
                        </p>
                    </div>

                    <section className="space-y-6">
                        <div className="flex items-center gap-2 border-b pb-2">
                            <h2 className="text-2xl font-bold">Gems Boosts</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {loading ? (
                                Array(3).fill(0).map((_, i) => <div key={i} className="h-64 bg-card/50 animate-pulse rounded-2xl" />)
                            ) : boosts.map(item => (
                                <Card key={item.id} className="glass-card hover:border-primary/50 transition-all group overflow-hidden rounded-custom-paddings">
                                    <div className="h-24 bg-gradient-to-r from-primary/10 to-secondary/10 flex items-center justify-center relative overflow-hidden rounded-custom-paddings">
                                        <img src={x2gems} alt="x2gems" className="w-16 h-16 text-primary/40 group-hover:scale-110 transition-transform" />
                                    </div>
                                    <CardHeader>
                                        <CardTitle>{item.name}</CardTitle>
                                        <CardDescription>{item.description}</CardDescription>
                                    </CardHeader>
                                    <CardFooter className="flex flex-col gap-4">
                                        {activeBoostEndDate ? (
                                            <div className="w-full space-y-2">
                                                <Button
                                                    variant="ghost"
                                                    disabled
                                                    className="w-full rounded-xl px-8 cursor-not-allowed bg-black/30"
                                                >
                                                    Already Active
                                                </Button>
                                                <p className="text-xs text-muted-foreground text-center">
                                                    A boost is already active until {new Date(activeBoostEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between w-full">
                                                <span className="text-2xl font-bold">{item.price_eur}€</span>
                                                <Button
                                                    onClick={() => handleBuy(item)}
                                                    disabled={buyingStatus === item.id}
                                                    className="rounded-xl px-8"
                                                >
                                                    {buyingStatus === item.id ? "Processing..." : "Buy Now"}
                                                </Button>
                                            </div>
                                        )}
                                    </CardFooter>
                                </Card>
                            ))}
                        </div>
                    </section>

                    <section className="space-y-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-2">
                            <div className="flex items-center gap-2">
                                <h2 className="text-2xl font-bold">Avatar Borders</h2>
                            </div>

                            <div className="flex items-center gap-2">
                                <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
                                    <SelectTrigger className="w-[180px] glass-card h-9">
                                        <SelectValue placeholder="Filter by status" />
                                    </SelectTrigger>
                                    <SelectContent className="glass-card border-none">
                                        <SelectItem value="all">All borders</SelectItem>
                                        <SelectItem value="owned">Owned</SelectItem>
                                        <SelectItem value="not_owned">Not owned</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            {loading ? (
                                Array(4).fill(0).map((_, i) => <div key={i} className="h-64 bg-card/50 animate-pulse rounded-2xl" />)
                            ) : borders.map(item => (
                                <Card key={item.id} className="glass-card hover:border-blue-500/50 transition-all group overflow-hidden rounded-custom-paddings flex flex-col justify-between">
                                    <div className="rounded-custom-paddings bg-slate-700/10 mb-3 h-full">
                                        <div className="h-24 flex items-center justify-center">
                                            <UserAvatar
                                                size="lg"
                                                borderStyle={item.metadata.style}
                                                src={userProfile?.avatar_url}
                                                fallback={userProfile?.display_name?.charAt(0) || "?"}
                                            />
                                        </div>
                                        <CardHeader>
                                            <CardTitle className="text-lg">{item.name}</CardTitle>
                                            <CardDescription className="text-xs">{item.description}</CardDescription>
                                        </CardHeader>
                                    </div>
                                    <CardFooter className="flex flex-col gap-4 pt-0">
                                        <div className="flex items-center justify-between w-full">
                                            <span className="text-xl font-bold">{item.price_eur}€</span>
                                            {ownedItemIds.includes(item.id) ? (
                                                <Button
                                                    variant="ghost"
                                                    disabled
                                                    className="rounded-xl px-4 text-xs cursor-not-allowed bg-black/30"
                                                >
                                                    Owned
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant={(item.metadata.style === 'beta' || item.metadata.style === 'neon' || item.metadata.style === 'gold') ? "default" : "secondary"}
                                                    onClick={() => handleBuy(item)}
                                                    disabled={buyingStatus === item.id}
                                                    className={cn(
                                                        "rounded-xl px-4 text-xs",
                                                        (item.metadata.style === 'beta' || item.metadata.style === 'neon' || item.metadata.style === 'gold') && "bg-purple-500 hover:bg-purple-600 border-none shadow-[0_0_15px_rgba(168,85,247,0.4)]"
                                                    )}
                                                >
                                                    {buyingStatus === item.id ? "..." : "Unlock"}
                                                </Button>
                                            )}
                                        </div>
                                    </CardFooter>
                                </Card>
                            ))}
                        </div>
                    </section>
                </div>
            </main>
        </Layout>
    );
};

export default Shop;
